/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  Trophy, Crown, Award, Sparkles, Users, Gift, Star, Calendar,
  Lock, Mail, Check, Loader2, Bell,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import CompetitionCard from '../components/CompetitionCard';
import WinnerCard from '../components/WinnerCard';
import BadgeCard from '../components/BadgeCard';
import SubmissionModal from '../components/SubmissionModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'live', label: 'Live Competitions', icon: Trophy },
  { id: 'winners', label: 'Hall of Fame', icon: Crown },
  { id: 'leaderboards', label: 'Leaderboards', icon: Award },
  { id: 'achievements', label: 'Achievements', icon: Star },
  { id: 'giveaways', label: 'Giveaways', icon: Gift },
];

const Competitions = () => {
  const { user, token } = useAuth();
  const [tab, setTab] = useState('live');
  const [launch, setLaunch] = useState(null);

  useEffect(() => {
    // Single source of truth for the founding-season state. Same shape for
    // anon + auth — admins get an extra `notify_count`.
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/competitions/launch-status`, { headers })
      .then((r) => setLaunch(r.data))
      .catch(() => setLaunch({ public_launch_enabled: true })); // fail-open to real UI
  }, [token]);

  const softLaunch = launch && !launch.public_launch_enabled;

  return (
    <div className="min-h-screen p-6 md:p-8" data-testid="competitions-page">
      <div className="max-w-7xl mx-auto">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <p className="data-label text-[#c8b273] mb-2">Compete · Win · Belong</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-extrabold uppercase tracking-wider text-white mb-4">
            Competitions & Hall of Fame
          </h1>
          <p className="text-[#8a948a] max-w-2xl mx-auto">
            {softLaunch
              ? 'Founding season is on. Sponsor-backed prizes drop the moment the partnership wave lands — and early members get priority access.'
              : 'Monthly competitions, exclusive giveaways and badge milestones — the heartbeat of Carp Cast.'}
          </p>
        </motion.div>

        {softLaunch && <FoundingSeasonHero launch={launch} user={user} token={token} />}

        {/* Tab navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="competitions-tabs">
          {TABS.map((t) => {
            const TabIcon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded text-xs font-bold uppercase tracking-widest transition-colors ${
                  active
                    ? 'bg-[#c8b273] text-black'
                    : 'border border-white/10 text-[#8a948a] hover:text-white hover:border-[#c8b273]/40'
                }`}
              >
                <TabIcon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Panel */}
        <div className="min-h-[600px]">
          {tab === 'live' && <LivePanel user={user} token={token} softLaunch={softLaunch} launch={launch} />}
          {tab === 'winners' && <WinnersPanel softLaunch={softLaunch} />}
          {tab === 'leaderboards' && <LeaderboardsPanel user={user} />}
          {tab === 'achievements' && <AchievementsPanel user={user} token={token} />}
          {tab === 'giveaways' && <GiveawaysPanel user={user} token={token} softLaunch={softLaunch} launch={launch} />}
        </div>
      </div>
    </div>
  );
};


// ==================== FOUNDING SEASON HERO + NOTIFY ====================

const FoundingSeasonHero = ({ launch, user, token }) => {
  const adminCount = launch?.notify_count;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative glass-card mb-10 overflow-hidden border-[#c8b273]/30"
      data-testid="founding-season-hero"
    >
      <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#c8b273]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-20 w-72 h-72 bg-[#7d6730]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative p-6 md:p-8 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#c8b273]/15 border border-[#c8b273]/30 text-[#c8b273] text-[10px] font-bold uppercase tracking-[0.25em] mb-4" data-testid="founding-season-badge">
            <Sparkles className="w-3 h-3" /> Founding Season
          </div>
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white mb-3">
            {launch?.headline || 'Launching Soon'}
          </h2>
          <p className="text-[#c0c4c0] text-sm md:text-base max-w-xl">
            {launch?.subhead}
          </p>
          {Array.isArray(launch?.placeholder_prizes) && launch.placeholder_prizes.length > 0 && (
            <ul className="mt-5 space-y-2" data-testid="founding-season-prize-list">
              {launch.placeholder_prizes.map((p) => (
                <li key={p} className="flex items-center gap-2 text-sm text-[#e2e4e2]">
                  <Trophy className="w-4 h-4 text-[#c8b273] flex-shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}
          {user?.is_admin && typeof adminCount === 'number' && (
            <p className="mt-4 text-[10px] uppercase tracking-[0.25em] text-[#c8b273]/80" data-testid="admin-notify-count">
              Admin · {adminCount} on the notify list
            </p>
          )}
        </div>
        <NotifyMeCard token={token} user={user} />
      </div>
    </motion.div>
  );
};

const NotifyMeCard = ({ token, user }) => {
  const [email, setEmail] = useState(user?.email || '');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // If the auth user finishes loading after first render, hydrate the field.
  useEffect(() => { if (user?.email && !email) setEmail(user.email); }, [user, email]);

  const submit = async (e) => {
    e.preventDefault();
    const value = (email || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error('Please enter a valid email');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: value,
        interest: 'competitions',
        name: user?.username || undefined,
      }, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
      setDone(true);
      toast.success("You're on the notify list — we'll email you when prizes drop.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save — try again');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-[#0a0c0a] border border-[#c8b273]/30 rounded-lg p-5 text-center" data-testid="notify-me-success">
        <Check className="w-8 h-8 text-[#22c55e] mx-auto mb-2" />
        <p className="text-white font-bold">You're on the list</p>
        <p className="text-[#8a948a] text-sm mt-1">We'll email you the moment competitions go live.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-[#0a0c0a] border border-[#c8b273]/30 rounded-lg p-5 space-y-3" data-testid="notify-me-card">
      <div className="flex items-center gap-2">
        <Bell className="w-4 h-4 text-[#c8b273]" />
        <p className="text-xs uppercase tracking-[0.2em] text-[#c8b273] font-bold">Notify me</p>
      </div>
      <p className="text-sm text-[#e2e4e2]">
        Be first in line. Drop your email and we'll ping you the moment the first sponsor-backed competition opens.
      </p>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7d6b]" />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="w-full bg-[#141714] border border-white/10 rounded px-3 py-2 pl-9 text-white text-sm placeholder-[#6b7d6b] focus:outline-none focus:border-[#c8b273]/50"
          required
          data-testid="notify-me-email"
        />
      </div>
      <button
        type="submit"
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 rounded bg-[#c8b273] hover:bg-[#d8c283] text-black text-xs font-bold uppercase tracking-widest px-4 py-2.5 transition-colors disabled:opacity-60"
        data-testid="notify-me-submit"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
        Notify me when competitions launch
      </button>
      <p className="text-[10px] text-[#6b7d6b] text-center">No spam · unsubscribe anytime</p>
    </form>
  );
};


// ==================== LIVE COMPETITIONS ====================

const LivePanel = ({ user, token, softLaunch, launch }) => {
  const [comps, setComps] = useState(null);
  const [submitFor, setSubmitFor] = useState(null);

  const fetchComps = () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/competitions?active_only=true`, { headers })
      .then((r) => setComps(r.data))
      .catch(() => setComps([]));
  };

  useEffect(() => { fetchComps(); }, [token]);

  const handleView = (c) => {
    if (softLaunch && !user?.is_admin) {
      toast.info('Competitions launch soon — drop your email above to be notified.');
      return;
    }
    if (!user) {
      toast.info('Sign in to enter the competition');
      return;
    }
    setSubmitFor(c);
  };

  if (!comps) return <SkeletonGrid />;

  // SOFT LAUNCH — replace live cards with a placeholder showcase. The hero
  // already carries the notify-me CTA so we keep this panel calm + premium.
  if (softLaunch && !user?.is_admin) {
    return <SoftLaunchShowcase launch={launch} variant="competitions" />;
  }

  if (!comps.length) return <EmptyState label="No live competitions right now — check back soon." />;

  const featured = comps.filter((c) => c.is_pinned && !c.locked);
  const others = comps.filter((c) => !featured.includes(c));

  return (
    <div className="space-y-10">
      {featured.length > 0 && (
        <section data-testid="featured-section">
          <SectionHeader title="Featured Right Now" icon={Sparkles} accent />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((c) => <CompetitionCard key={c.id} competition={c} onView={handleView} />)}
          </div>
        </section>
      )}

      <section data-testid="all-competitions-section">
        <SectionHeader title="All Active Competitions" icon={Trophy} />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {others.filter((c) => c.competition_type !== 'giveaway').map((c) => (
            <CompetitionCard key={c.id} competition={c} onView={handleView} />
          ))}
        </div>
      </section>

      <SubmissionModal
        open={!!submitFor}
        competition={submitFor}
        token={token}
        onClose={() => setSubmitFor(null)}
        onSuccess={fetchComps}
      />
    </div>
  );
};


// ==================== SOFT-LAUNCH PLACEHOLDER SHOWCASE ====================

const SoftLaunchShowcase = ({ launch, variant }) => {
  // Three premium placeholder "slots" — same look-and-feel as the live
  // CompetitionCard so the page feels populated, not broken. Each slot uses
  // the placeholder copy that the backend ships, plus a locked badge.
  const prizes = launch?.placeholder_prizes || [
    'Exclusive Sponsor Prizes Coming Soon',
    'Premium Carp Fishing Rewards',
    'Future Fishery Tickets & Gear Giveaways',
  ];
  const subs = variant === 'giveaways'
    ? ['Premium tackle drops', 'Sponsor-backed fishery tickets', 'Limited founding-member rewards']
    : ['Catch of the season', 'Photo of the month', 'Lake-record contender'];
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={`softlaunch-showcase-${variant}`}>
      {prizes.map((p, i) => (
        <div
          key={p}
          className="relative glass-card overflow-hidden border-[#c8b273]/20"
          data-testid={`softlaunch-card-${variant}-${i}`}
        >
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-[#c8b273]/8 rounded-full blur-3xl pointer-events-none" />
          <div className="relative h-32 bg-gradient-to-br from-[#1a1f1a] via-[#0b0d0b] to-[#1a141a] flex items-center justify-center">
            <Trophy className="w-10 h-10 text-[#c8b273]/40" />
            <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-[#c8b273]/15 text-[#c8b273] border border-[#c8b273]/30">
              <Lock className="w-3 h-3" /> Launching Soon
            </span>
            <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-black/40 text-[#c8b273] border border-[#c8b273]/30 backdrop-blur">
              <Sparkles className="w-3 h-3" /> Founding
            </span>
          </div>
          <div className="p-5">
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wider text-white mb-1.5">
              {subs[i] || 'Coming Soon'}
            </h3>
            <p className="text-sm text-[#8a948a] mb-4 line-clamp-2">
              Sponsor-backed prizes coming soon. Early members get priority access.
            </p>
            <div className="flex items-center gap-2 text-sm mb-4">
              <Trophy className="h-4 w-4 text-[#c8b273] flex-shrink-0" />
              <span className="text-[#e2e4e2] truncate">{p}</span>
            </div>
            <div className="mt-3 p-3 rounded border border-[#c8b273]/25 bg-[#c8b273]/5 flex items-center gap-2">
              <Lock className="h-4 w-4 text-[#c8b273] flex-shrink-0" />
              <p className="text-xs text-[#c8b273]">Early members get priority access when this drops.</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};


// ==================== MONTHLY WINNERS ====================

const WinnersPanel = ({ softLaunch }) => {
  const [winners, setWinners] = useState(null);
  useEffect(() => {
    axios.get(`${API}/winners?limit=50`).then((r) => setWinners(r.data)).catch(() => setWinners([]));
  }, []);

  if (!winners) return <SkeletonGrid />;
  if (!winners.length) {
    return (
      <div className="space-y-6">
        <EmptyState
          label={softLaunch ? 'The Hall of Fame is reserved for the founding cast.' : 'The Hall of Fame is just getting started.'}
          subLabel={softLaunch
            ? 'Winners will be inducted from the first sponsor-backed competition season — and early members are first in.'
            : 'Be the first to enter a competition — your name could be here next month.'}
        />
      </div>
    );
  }

  return (
    <div>
      <SectionHeader title="Hall of Fame" icon={Crown} accent />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {winners.map((w) => <WinnerCard key={w.id} winner={w} />)}
      </div>
    </div>
  );
};


// ==================== LEADERBOARDS ====================

const LeaderboardsPanel = ({ user }) => {
  const [leaderboard, setLeaderboard] = useState(null);
  const [fishType, setFishType] = useState('');

  useEffect(() => {
    const q = fishType ? `?fish_type=${encodeURIComponent(fishType)}` : '';
    axios.get(`${API}/leaderboard${q}`).then((r) => setLeaderboard(r.data)).catch(() => setLeaderboard([]));
  }, [fishType]);

  const fishOptions = ['', 'Common Carp', 'Mirror Carp', 'Leather Carp', 'Grass Carp'];

  return (
    <div>
      <SectionHeader title="Biggest Fish Leaderboard" icon={Award} accent />
      <div className="flex flex-wrap gap-2 mb-6" data-testid="leaderboard-filters">
        {fishOptions.map((opt) => (
          <button
            key={opt || 'all'}
            type="button"
            onClick={() => setFishType(opt)}
            className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-widest transition-colors ${
              fishType === opt ? 'bg-[#c8b273] text-black' : 'border border-white/10 text-[#8a948a] hover:text-white'
            }`}
          >
            {opt || 'All Species'}
          </button>
        ))}
      </div>

      {!leaderboard ? (
        <SkeletonGrid rows={5} cols={1} />
      ) : !leaderboard.length ? (
        <EmptyState label="No leaderboard entries yet." />
      ) : (
        <div className="glass-card overflow-hidden">
          <div className="grid grid-cols-12 px-5 py-3 bg-[#0b0d0b] border-b border-white/10 text-[#8a948a] text-xs font-bold uppercase tracking-widest">
            <div className="col-span-1">#</div>
            <div className="col-span-5">Angler</div>
            <div className="col-span-3">Fish</div>
            <div className="col-span-2">Lake</div>
            <div className="col-span-1 text-right">Weight</div>
          </div>
          {leaderboard.map((row, i) => (
            <div
              key={`${row.id || i}`}
              data-testid={`leaderboard-row-${i}`}
              className={`grid grid-cols-12 px-5 py-3 items-center text-sm ${
                i < 3 ? 'bg-[#c8b273]/5' : ''
              } ${i % 2 === 1 ? 'bg-[#0a0b0a]/40' : ''} border-b border-white/5`}
            >
              <div className={`col-span-1 font-['Barlow_Condensed'] text-xl font-bold ${
                i === 0 ? 'text-[#c8b273]' : i < 3 ? 'text-white' : 'text-[#8a948a]'
              }`}>
                {i + 1}
              </div>
              <div className="col-span-5 text-white truncate">
                {row.username || row.user || 'Angler'}
                {i === 0 && <Crown className="inline-block w-4 h-4 ml-2 text-[#c8b273]" />}
              </div>
              <div className="col-span-3 text-[#8a948a] truncate">{row.fish_type || '—'}</div>
              <div className="col-span-2 text-[#8a948a] truncate text-xs">{row.lake_name || '—'}</div>
              <div className="col-span-1 text-right text-[#c8b273] font-bold">
                {row.weight ? `${row.weight} lb` : '—'}
              </div>
            </div>
          ))}
        </div>
      )}

      {user?.subscription_tier === 'basic' && (
        <div className="mt-6 glass-card p-5 border-[#c8b273]/30 bg-[#c8b273]/5 flex items-center justify-between">
          <div>
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white">
              Pro members get more leaderboards
            </h3>
            <p className="text-sm text-[#8a948a]">Monthly league rankings, top contributor &amp; most-active boards unlock with Pro.</p>
          </div>
          <a
            href="/subscription"
            data-testid="leaderboard-upgrade-cta"
            className="inline-flex items-center gap-2 rounded bg-[#c8b273] px-4 py-2 text-sm font-bold uppercase tracking-wide text-black hover:bg-[#d8c283]"
          >
            <Crown className="h-4 w-4" /> Upgrade
          </a>
        </div>
      )}
    </div>
  );
};


// ==================== ACHIEVEMENTS ====================

const AchievementsPanel = ({ user, token }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!token) {
      // Anon: just show the catalog as locked
      axios.get(`${API}/badges/catalog`).then((r) => {
        setData({ earned: [], locked: r.data, earned_count: 0, total: r.data.length });
      }).catch(() => setData({ earned: [], locked: [], earned_count: 0, total: 0 }));
      return;
    }
    axios.get(`${API}/badges/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData({ earned: [], locked: [], earned_count: 0, total: 0 }));
  }, [token]);

  if (!data) return <SkeletonGrid />;

  const pct = data.total ? Math.round((data.earned_count / data.total) * 100) : 0;

  return (
    <div>
      {/* Progress header */}
      <div className="glass-card p-5 mb-8" data-testid="badge-progress">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="data-label text-[#c8b273] mb-1">Your Progress</p>
            <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white">
              {data.earned_count} / {data.total} Badges
            </h3>
          </div>
          <div className="text-right">
            <p className="font-['Barlow_Condensed'] text-3xl font-extrabold text-[#c8b273]">{pct}%</p>
            <p className="text-xs text-[#8a948a] uppercase tracking-widest">Complete</p>
          </div>
        </div>
        <div className="h-2 rounded-full bg-[#1f221f] overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#c8b273] to-[#d8c283]"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {data.earned.length > 0 && (
        <section className="mb-10">
          <SectionHeader title="Earned" icon={Star} accent />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {data.earned.map((b) => (
              <BadgeCard key={b.code} badge={b} earned />
            ))}
          </div>
        </section>
      )}

      <section>
        <SectionHeader title="Locked" icon={Trophy} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {data.locked.map((b) => (
            <BadgeCard key={b.code} badge={b} />
          ))}
        </div>
      </section>
    </div>
  );
};


// ==================== GIVEAWAYS ====================

const GiveawaysPanel = ({ user, token, softLaunch, launch }) => {
  const [giveaways, setGiveaways] = useState(null);
  const [submitFor, setSubmitFor] = useState(null);

  const fetchGiveaways = () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/competitions?active_only=true`, { headers })
      .then((r) => setGiveaways(r.data.filter((c) => c.competition_type === 'giveaway')))
      .catch(() => setGiveaways([]));
  };

  useEffect(() => { fetchGiveaways(); }, [token]);

  if (!giveaways) return <SkeletonGrid />;

  // Soft-launch: replace the live giveaway grid with the founding-season
  // placeholder showcase. Real prize text is already scrubbed server-side
  // for non-admins, but we hide the auto-entry banner too because it
  // implies the system is live.
  if (softLaunch && !user?.is_admin) {
    return (
      <div data-testid="giveaways-softlaunch">
        <div className="glass-card p-5 mb-8 bg-gradient-to-r from-[#c8b273]/8 to-transparent border-[#c8b273]/30">
          <div className="flex items-start gap-3">
            <Gift className="w-7 h-7 text-[#c8b273] flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white">
                Giveaways launching soon
              </h3>
              <p className="text-sm text-[#8a948a] mt-1">
                Sponsor-backed prize draws drop the moment the partnership wave lands. Founding members go first.
              </p>
            </div>
          </div>
        </div>
        <SoftLaunchShowcase launch={launch} variant="giveaways" />
      </div>
    );
  }

  if (!giveaways.length) return <EmptyState label="No active giveaways — next draw coming soon." />;

  return (
    <div>
      <div className="glass-card p-5 mb-8 bg-gradient-to-r from-[#c8b273]/8 to-transparent border-[#c8b273]/30">
        <div className="flex items-start gap-3">
          <Gift className="w-7 h-7 text-[#c8b273] flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white">
              Auto-entry for Pro & Elite members
            </h3>
            <p className="text-sm text-[#8a948a] mt-1">
              Your Carp Cast membership automatically enters you into every giveaway at your tier. No clicks. No spam. Just prizes.
            </p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {giveaways.map((c) => (
          <CompetitionCard
            key={c.id}
            competition={c}
            onView={() => (user ? setSubmitFor(c) : toast.info('Sign in to enter'))}
          />
        ))}
      </div>

      <SubmissionModal
        open={!!submitFor}
        competition={submitFor}
        token={token}
        onClose={() => setSubmitFor(null)}
        onSuccess={fetchGiveaways}
      />
    </div>
  );
};


// ==================== SHARED ====================

const SectionHeader = ({ title, icon: Icon, accent = false }) => (
  <div className="flex items-center gap-3 mb-5">
    <Icon className={`h-5 w-5 ${accent ? 'text-[#c8b273]' : 'text-[#8a948a]'}`} />
    <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white">
      {title}
    </h2>
  </div>
);

const SkeletonGrid = ({ rows = 6, cols = 3 }) => (
  <div className={`grid gap-5 ${cols === 1 ? '' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="glass-card h-64 animate-pulse">
        <div className="h-32 bg-[#1f221f]" />
        <div className="p-5 space-y-3">
          <div className="h-4 w-3/4 bg-[#1f221f] rounded" />
          <div className="h-3 w-full bg-[#1f221f] rounded" />
          <div className="h-3 w-1/2 bg-[#1f221f] rounded" />
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = ({ label, subLabel }) => (
  <div className="glass-card p-10 text-center">
    <Trophy className="h-10 w-10 text-[#c8b273] mx-auto mb-4" />
    <p className="text-white text-lg font-['Barlow_Condensed'] uppercase tracking-wider mb-1">{label}</p>
    {subLabel && <p className="text-sm text-[#8a948a]">{subLabel}</p>}
  </div>
);

export default Competitions;
